import type { BrowserTicket } from '@repo/contracts/browser-protocol';

import { apiFetch } from '../api/client';

/**
 * Exchanges the app session for a short-lived browser-worker credential.
 *
 * Not a TanStack Query hook, deliberately: a ticket is not server *state* to be
 * cached and shared — it is a single-use credential fetched at the moment a
 * socket is opened, and a cached one would go stale silently (it expires in
 * minutes) with nothing to invalidate it. The transport calls this directly.
 */
export function fetchBrowserTicket(): Promise<BrowserTicket> {
  return apiFetch<BrowserTicket>('/canvas/browser-ticket', { method: 'POST' });
}

/**
 * Resolves the ticket's `workerUrl` to an absolute ws(s):// URL.
 *
 * A relative value ("/browser", the dev default) is resolved against the page
 * origin so it goes through the Vite proxy — which matters beyond convenience:
 * the socket then inherits the page's scheme, so an https page never opens an
 * insecure ws:// the browser would block outright.
 */
export function workerSocketUrl(ticket: BrowserTicket): URL {
  const base = ticket.workerUrl.startsWith('/')
    ? new URL(`${window.location.origin}${ticket.workerUrl}`)
    : new URL(ticket.workerUrl);

  const url = new URL(`${base.pathname.replace(/\/$/, '')}/live`, base);
  url.protocol =
    base.protocol === 'http:' || base.protocol === 'ws:' ? 'ws:' : 'wss:';
  url.searchParams.set('token', ticket.token);
  return url;
}
